<script setup lang="ts">
import { GpBtn, GpTable, GpTableColumn } from '@gportal/uikit';
import { faCheck, faTimes, faTrash } from '@fortawesome/pro-solid-svg-icons';
import { ImageFormatType, type ImageVersion } from 'gpcore-grpc/gpcore/api/cloud/v1/image_pb';
import { toRef, useAttrs } from 'vue';
import useGpTableData from '@panel/composables/useGpTableData';
import ConfirmModal from 'gpcore-uikit/components/new/ConfirmModal.vue';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import useDateFormat from 'gpcore-uikit/composables/useDateFormat';
import ComponentEnumTag from 'gpcore-uikit/components/Component.EnumTag.vue';
import { type VersionsTableRow } from './composables/useImageVersions';
import useDeleteImageVersions from './composables/useDeleteImageVersions';

const props = defineProps<{ versions: Array<ImageVersion> }>();

const emit = defineEmits<{ deleted: [] }>();

const projectImageVersions = toRef(props, 'versions');
const format = useDateFormat();
const { tableData } = useGpTableData(projectImageVersions, (data) =>
  data.map((i) => ({
    key: i.id,
    available: i.available,
    format: i.format,
    checksum: i.checksum.checksum,
    updatedAt: format(i.updatedAt, 'short'),
  })),
);

const formatDate = (date: number) => {
  if (date == null) {
    return 'n.A.';
  }

  return format(new Date(date));
};

const { deleteVersion, isDeleteVersionModalOpened, isDeletingVersion, onDeleteVersion } =
  useDeleteImageVersions();

const deleteImageVersion = async () => {
  await deleteVersion();
  emit('deleted');
};

const attrs = useAttrs();
</script>

<template>
  <GpTable :class="attrs.class" :data="tableData">
    <GpTableColumn field="checksum" label="Checksum" :column-style="{ 'line-break': 'anywhere' }">
      <template #body="{ data }: { data: VersionsTableRow['checksum'] }">
        <template v-if="data != null && data.length > 0">{{ data }}</template>
        <template v-else>n.A.</template>
      </template>
    </GpTableColumn>

    <GpTableColumn field="format" label="Format">
      <template #body="{ data }: { data: VersionsTableRow['format'] }">
        <ComponentEnumTag :enum-map="ImageFormatType" :enum-value="data" />
      </template>
    </GpTableColumn>

    <GpTableColumn field="available" label="Available">
      <template #body="{ data }: { data: VersionsTableRow['available'] }">
        <FontAwesomeIcon v-if="data" :icon="faCheck" class="tw-text-gp-green" />
        <FontAwesomeIcon v-else :icon="faTimes" class="tw-text-gp-red" />
      </template>
    </GpTableColumn>

    <GpTableColumn field="updatedAt" label="Updated at">
      <template #body="{ data }: { data: VersionsTableRow['updatedAt'] }">
        {{ formatDate(data) }}
      </template>
    </GpTableColumn>

    <GpTableColumn field="actions" column-class="tw-text-right">
      <template #body="{ row }">
        <GpBtn
          size="sm"
          btn-type="danger"
          label="Remove"
          :icon="faTrash"
          outline
          @click="onDeleteVersion(row.key)"
        />
      </template>
    </GpTableColumn>
  </GpTable>

  <ConfirmModal
    :show="isDeleteVersionModalOpened"
    :loading="isDeletingVersion"
    :confirm-text="isDeletingVersion ? 'Removing ...' : 'Remove'"
    question="Are you sure you want to remove this version?"
    confirm-type="danger"
    @confirm="deleteImageVersion"
    @close="isDeleteVersionModalOpened = false"
  />
</template>
